/**
 * Display Device Config Form in Admin > Devices > Edit Device
 *
 * @author: Vanessa Richie Alia-Trapero <vanessa@dividia.net>
 */

import { useState, useEffect } from 'react'
import { connect } from 'react-redux';
import { showAlertModal } from '../../../../actions';
import ConfigGroup from './ConfigGroup'
import Dialog from '../../../common/Dialog';

const ConfigForm = (props) => {
  const {showAlertModal, deviceId, cameraId, config, reloadConfig, handleConfigReload, handleConfigChanges} = props

  const [cameraPage, setCameraPage] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showPropertyDialog, setShowPropertyDialog] = useState(false)
  const [newProperty, setNewProperty] = useState({})
  const [nextCameraIndex, setNextCameraIndex] = useState(1)
  const [duplicates, setDuplicates] = useState([])

  // update this to include fields displayed on initial load of device config form
  // to display all properties in a section, put *, see sample below
  // eg: {global: ['*'], camera1: ['url1', 'motion_grid_mask', 'url2']}
  const basicFields = cameraPage ? {['camera' + cameraId]: ['*']} : {}

  useEffect(() => {
    if (reloadConfig) {
      // set timeout to ease the flicker caused by re-rendering after state changes
      setTimeout(() => {
        handleConfigReload()
        handleConfigChanges('reloadConfig', false)
        setDuplicates([])
      }, 200)

    } else {
      let lastIndex = 0
      Object.entries(config).forEach(([key]) => {
        let id = (key.startsWith('camera')) ? parseInt(key.slice('6')) : 0
        lastIndex = id > lastIndex ? id : lastIndex
      })
      setNextCameraIndex(lastIndex + 1)
      cameraId && setCameraPage(true)
    }

  // eslint-disable-next-line
  }, [config, reloadConfig])

  const toggleAdvancedDisplay = () => {
    if (showAdvanced) {
      setShowAdvanced(false)
    } else {
      setShowAdvanced(true)
    }
  }

  const handleInputChange = (e) => {
    // update config data for each change in the input fields so we can pass it easily in the save api call
    const target = e.target
    const group = target.dataset.group
    const name = target.name
    let value = target.value
    let conf = {...config}

    if (target.type === 'checkbox') {
      value = (target.checked) ? '1' : '0'
    }

    conf[group][name] = value
    handleConfigChanges('config', conf)
  }

  const handleGroupDuplicate = (group) => {
    // on duplicate, push the duplicate into the config data as well as on the duplicates array which will handle the duplicate background color
    let conf = {...config}
    const newCamera = {...conf[group]}
    let dups = [...duplicates]
    const dupIndex = 'camera' + nextCameraIndex

    dups.push(dupIndex)
    setDuplicates(dups)

    conf[dupIndex] = newCamera
    handleConfigChanges('config', conf)

    setNextCameraIndex(nextCameraIndex + 1)
  }

  const handleGroupDelete = (group) => {
    // transfer the focus first before deleting so that the onblur will work
    document.getElementById("config-form").focus()

    // on delete, update the config data, react will handle re-rendering the page since data state has been updated
    let conf = {...config}
    delete conf[group]
    handleConfigChanges('config', conf)
  }

  const handleNewProperty = (show, group) => {
    if (show) {
      const prop = {group: group}
      setNewProperty(prop)
      setShowPropertyDialog(true)
    } else {
      setNewProperty({})
      setShowPropertyDialog(false)
    }
  }

  const handleNewPropertyChange = (e) => {
    const prop = {...newProperty}
    prop[e.target.name] = e.target.value
    setNewProperty(prop)
  }

  const addNewProperty = (type) => {
    // check if key and value has been filled
    if (!newProperty.key || !newProperty.value) {
      showAlertModal(['WARNING', 'Please fill in both property key and value.'])
      return
    }

    // check if key already exists
    if ((type === 'group' && Object.entries(config[newProperty.group]).find(([key, _]) => key === newProperty.key))
      || (type === 'cameras' && Object.entries(config).find(([group, fields]) => group.startsWith('camera') && Object.entries(fields).find(([key, _]) => key === newProperty.key)))) {
      showAlertModal(['WARNING', 'Property key already exists. Please review.'])
      return
    }

    // transfer the focus back to the form so that the onblur will work
    document.getElementById("config-form").focus()

    // if no more errors, add the property to our device config object (data)
    let conf = {...config}
    Object.entries(config).forEach(([key, val]) => {
      if ((type === 'cameras' && key.startsWith('camera')) || (type === 'group' && key === newProperty.group)) {
        val[newProperty.key] = newProperty.value
      }
      conf[key] = val
    })
    handleConfigChanges('config', conf)
    handleNewProperty(false)
  }

  const newPropertyDialog =
    <div style={styles.newPropertyContainer}>
      <div style={styles.newPropertyField}>
        <label style={styles.newPropertyLabel}>Property</label>
        <input style={styles.newPropertyInput} type="text" name="key" onChange={handleNewPropertyChange} />
      </div>
      <div style={styles.newPropertyField}>
        <label style={styles.newPropertyLabel}>Value</label>
        <input style={styles.newPropertyInput} type="text" name="value" onChange={handleNewPropertyChange} />
      </div>
      <input type="hidden" name="group" value={newProperty.group} />
    </div>

  return (
    <div id="config-form" tabIndex="0">
      <div style={styles.header}>
        <div style={styles.headerLabel}>Device Config</div>
        {!cameraPage && <button style={styles.headerButton} onClick={toggleAdvancedDisplay}>{showAdvanced ? 'Hide' : 'Show'} Advanced Config</button>}
      </div>
      {Object.entries(config).map(([group, fields]) => {
        const displayGroup = (showAdvanced || Object.keys(basicFields).includes(group))
        return displayGroup &&
          <ConfigGroup
            deviceId={deviceId}
            key={group}
            name={group}
            fields={fields}
            basicFields={basicFields[group]}
            duplicate={duplicates.includes(group) ? true : false}
            showAdvanced={showAdvanced}
            cameraPage={cameraPage}
            handleInputChange={handleInputChange}
            handleGroupDuplicate={handleGroupDuplicate}
            handleGroupDelete={handleGroupDelete}
            handleNewProperty={handleNewProperty} />
      })}
      {showPropertyDialog &&
        <Dialog
          title="Add New Config Property"
          content={newPropertyDialog}
          buttons={[
            ...(cameraPage
              ? [{label: 'Add', onClick: () => addNewProperty('group')}]
              : [{label: 'Add to all cameras', onClick: () => addNewProperty('cameras')},
                {label: 'Add to "' + newProperty.group + '" only', onClick: () => addNewProperty('group')},]
            ),
            {label: 'Cancel', onClick: () => handleNewProperty(false)}
          ]} />
      }
    </div>
  )
}

const mapStateToProps = (state) => {
  return {}
}
export default connect(mapStateToProps, { showAlertModal } )(ConfigForm);

const styles = {
  header: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '1vmin',
  },
  headerLabel: {
    fontSize: '2vmin',
    fontWeight: 'bold',
    margin: '2vmin 0 1vmin',
  },
  headerButton: {
    cursor: 'pointer',
    fontSize: '1.4vmin',
  },
  newPropertyContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1vmin',
  },
  newPropertyField: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  newPropertyLabel: {
    fontSize: '1.6vmin',
  },
  newPropertyInput: {
    width: '70%',
  },
}